import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MapPin, Clock, Car, Navigation2, Fuel, Wrench, CheckCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const user = localStorage.getItem('user');
  if (user) {
    const userData = JSON.parse(user);
    if (userData.token) {
      return { Authorization: `Bearer ${userData.token}` };
    }
  }
  return {};
};

const QuoteSubmissionCard = ({ request, onQuoteSubmitted }) => {
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quoteData, setQuoteData] = useState({
    price: '',
    estimatedArrival: '15',
    message: ''
  });

  const handleSubmitQuote = async () => {
    if (!quoteData.price || parseFloat(quoteData.price) <= 0) {
      toast({
        title: 'Invalid Price',
        description: 'Please enter a valid quote price',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/service-requests/${request.id}/submit-quote`,
        {
          service_request_id: request.id,
          price: parseFloat(quoteData.price),
          estimated_arrival_minutes: parseInt(quoteData.estimatedArrival),
          message: quoteData.message
        },
        { headers: getAuthHeader() }
      );

      toast({
        title: 'Quote Submitted!',
        description: 'Customer will see your quote'
      });

      setShowQuoteForm(false);
      if (onQuoteSubmitted) onQuoteSubmitted();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to submit quote',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(46, 178, 213, 0.3)' }} className="hover:border-wheely-orange/30/50 transition-colors">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-white text-xl mb-2">{request.service}</CardTitle>
            <p className="text-gray-300">{request.customer}</p>
          </div>
          <Badge style={{ background: 'rgba(235, 152, 104, 0.2)', color: '#EB9868' }}>
            New Request
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Service Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-start gap-2 text-gray-300">
              <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-wheely-orange-light" />
              <div>
                <p className="text-xs text-gray-400">Pickup Location:</p>
                <p className="text-white">{request.location}</p>
              </div>
            </div>

            {request.destination && (
              <div className="flex items-start gap-2 text-gray-300">
                <Navigation2 className="w-4 h-4 mt-1 flex-shrink-0 text-green-400" />
                <div>
                  <p className="text-xs text-gray-400">Drop-off:</p>
                  <p className="text-white">{request.destination}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 text-gray-300">
              <Car className="w-4 h-4 text-wheely-orange-light" />
              <span>{request.vehicleModel} ({request.vehicleYear})</span>
            </div>
          </div>

          <div className="space-y-3">
            {request.vehicleCondition && (
              <div style={{ background: 'rgba(15, 23, 42, 0.8)' }} className="p-3 rounded-lg">
                <p className="text-xs text-gray-400 mb-1">Vehicle Condition:</p>
                <p className="text-white font-semibold">
                  {request.vehicleCondition === 'starts_and_runs' && 'Starts & Runs'}
                  {request.vehicleCondition === 'rolls_neutral' && 'Rolls in Neutral'}
                  {request.vehicleCondition === 'keyless_stuck' && 'Keyless/Stuck (Winch Required)'}
                </p>
              </div>
            )}

            {request.tireSize && (
              <div style={{ background: 'rgba(15, 23, 42, 0.8)' }} className="p-3 rounded-lg">
                <p className="text-xs text-gray-400 mb-1">Tire Size:</p>
                <p className="text-white font-semibold">{request.tireSize}</p>
                {request.isAlloyWheel && (
                  <Badge style={{ background: 'rgba(235, 152, 104, 0.2)', color: '#EB9868' }} className="mt-1">Alloy Wheel</Badge>
                )}
              </div>
            )}

            {request.fuelType && (
              <div style={{ background: 'rgba(15, 23, 42, 0.8)' }} className="p-3 rounded-lg flex items-center gap-2">
                <Fuel className="w-4 h-4 text-wheely-orange-light" />
                <div>
                  <p className="text-xs text-gray-400">Fuel Type:</p>
                  <p className="text-white font-semibold capitalize">{request.fuelType}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Issue Description */}
        <div style={{ background: 'rgba(15, 23, 42, 0.8)' }} className="p-3 rounded-lg">
          <p className="text-gray-300 text-sm mb-1">Issue Description:</p>
          <p className="text-white">{request.issue}</p>
        </div>

        {/* Quote Form */}
        {!showQuoteForm ? (
          <Button
            onClick={() => setShowQuoteForm(true)}
            style={{ background: 'linear-gradient(to right, #E17431, #EB9868)' }}
            className="w-full hover:opacity-90 text-white font-semibold"
          >
            Submit Your Quote
          </Button>
        ) : (
          <div className="space-y-4 p-4 rounded-lg" style={{ background: 'rgba(15, 23, 42, 0.8)' }}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price" className="text-white">Your Quote Price (£) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g., 85.00"
                  value={quoteData.price}
                  onChange={(e) => setQuoteData({...quoteData, price: e.target.value})}
                  style={{ background: 'rgba(30, 58, 74, 0.6)', borderColor: 'rgba(46, 178, 213, 0.3)' }}
                  className="text-white"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="eta" className="text-white">Arrival Time (mins) *</Label>
                <Input
                  id="eta"
                  type="number"
                  min="1"
                  placeholder="15"
                  value={quoteData.estimatedArrival}
                  onChange={(e) => setQuoteData({...quoteData, estimatedArrival: e.target.value})}
                  style={{ background: 'rgba(30, 58, 74, 0.6)', borderColor: 'rgba(46, 178, 213, 0.3)' }}
                  className="text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-white">Message (Optional)</Label>
              <Textarea
                id="message"
                placeholder="Any additional information for the customer..."
                value={quoteData.message}
                onChange={(e) => setQuoteData({...quoteData, message: e.target.value})}
                rows={3}
                style={{ background: 'rgba(30, 58, 74, 0.6)', borderColor: 'rgba(46, 178, 213, 0.3)' }}
                className="text-white placeholder:text-gray-400 resize-none"
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleSubmitQuote}
                disabled={submitting}
                style={{ background: 'linear-gradient(to right, #10b981, #059669)' }}
                className="flex-1 hover:opacity-90 text-white font-semibold"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Submitting...
                  </span>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Submit Quote
                  </>
                )}
              </Button>
              <Button
                onClick={() => setShowQuoteForm(false)}
                variant="outline"
                style={{ borderColor: 'rgba(148, 163, 184, 0.5)', color: '#94a3b8' }}
                className="hover:bg-gray-500/10"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuoteSubmissionCard;
